"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useConsultantStore } from "@/app/store";

import {
	createGiftOfferClient,
	type GiftOfferClient
} from "./gift-offer-client";
import {
	readGiftRecipientParams,
	type GiftRecipientParams
} from "./gift-query-params";
import {
	isClaimFormValid,
	validateClaimForm,
	type ClaimFormErrors,
	type ClaimFormValues
} from "./validate-claim-form";

export type GiftRecipientLandingStatus =
	| "checking"
	| "landing"
	| "submitting"
	| "submit-error"
	| "success"
	| "ineligible";

export interface ConnectedConsultantSummary {
	name?: string;
	phone?: string;
	email?: string;
}

export interface GiftRecipientLandingState {
	status: GiftRecipientLandingStatus;
	recipient: GiftRecipientParams;
	values: ClaimFormValues;
	errors: ClaimFormErrors;
	consultant: ConnectedConsultantSummary;
}

export interface GiftRecipientLandingActions {
	setField(field: keyof ClaimFormValues, value: string): void;
	submit(): Promise<void>;
}

export interface UseGiftRecipientLandingOptions {
	client?: GiftOfferClient;
}

/**
 * Runs in the browser because everything it reads is per-visitor and the page
 * is cached. `unknown` eligibility fails open: the email already offered this.
 */
export function useGiftRecipientLanding(
	options: UseGiftRecipientLandingOptions = {}
): GiftRecipientLandingState & GiftRecipientLandingActions {
	const searchParams = useSearchParams();
	const consultantState = useConsultantStore(
		(state) => state.consultantState
	);
	const client = useMemo(
		() => options.client ?? createGiftOfferClient(),
		[options.client]
	);

	const recipient = useMemo(
		() =>
			readGiftRecipientParams(
				new URLSearchParams(searchParams.toString())
			),
		[searchParams]
	);

	const [status, setStatus] = useState<GiftRecipientLandingStatus>(
		recipient.email ? "checking" : "landing"
	);
	const [values, setValues] = useState<ClaimFormValues>({
		firstName: recipient.firstName ?? "",
		lastName: recipient.lastName ?? ""
	});
	const [errors, setErrors] = useState<ClaimFormErrors>({});

	// No "already checked" guard: React dev mode runs effects twice and the
	// cleanup below discards the first result, so a guard would freeze the page.
	useEffect(() => {
		const email = recipient.email;
		if (!email) return;

		let cancelled = false;
		client.checkEligibility(email).then((result) => {
			if (cancelled) return;
			setStatus(
				result.status === "not-eligible" ? "ineligible" : "landing"
			);
		});

		return () => {
			cancelled = true;
		};
	}, [client, recipient.email]);

	const setField = useCallback(
		(field: keyof ClaimFormValues, value: string) => {
			setValues((current) => ({ ...current, [field]: value }));
			setErrors((current) => {
				if (!current[field]) return current;
				const next = { ...current };
				delete next[field];
				return next;
			});
		},
		[]
	);

	const submit = useCallback(async () => {
		const validation = validateClaimForm(values);
		setErrors(validation);
		if (!isClaimFormValid(validation) || !recipient.email) return;

		setStatus("submitting");
		const result = await client.submitClaim({
			firstName: values.firstName.trim(),
			lastName: values.lastName.trim(),
			email: recipient.email
		});

		if (result.status === "success") setStatus("success");
		else if (result.status === "not-eligible") setStatus("ineligible");
		else setStatus("submit-error");
	}, [client, recipient.email, values]);

	const consultant: ConnectedConsultantSummary =
		consultantState.status === "connected"
			? {
					name: `${consultantState.firstName} ${consultantState.lastName}`.trim(),
					phone: consultantState.phoneNumber || undefined,
					email: consultantState.emailAddress || undefined
				}
			: {};

	return { status, recipient, values, errors, consultant, setField, submit };
}
